// src/services/firestoreService.js

import { db, auth } from './firebase'; // Firebase の初期化済み db、auth オブジェクトをインポート

import {
    collection,
    addDoc,
    getDocs,
    query,
    where,
    orderBy,
    doc,
    getDoc,
    updateDoc,
    deleteDoc,
    Timestamp, // <-- Timestamp が正しくインポートされていることを確認
    limit,
    startAfter
} from 'firebase/firestore';

// 'pages' コレクションへの参照を定義
const pagesCollection = collection(db, 'pages');

// タイムスタンプを処理するヘルパー関数
const processTimestamp = (timestamp) => {
    if (!timestamp) return null;
    if (typeof timestamp === 'object' && timestamp.seconds !== undefined) {
        return new Timestamp(timestamp.seconds, timestamp.nanoseconds);
    }
    return timestamp;
};

// ドキュメントデータを処理するヘルパー関数
const processDocumentData = (doc) => {
    const data = doc.data();
    return {
        id: doc.id,
        ...data,
        createdAt: processTimestamp(data.createdAt),
        updatedAt: processTimestamp(data.updatedAt)
    };
};

/**
 * 新しいページをFirestoreに作成します。
 * @param {object} pageData - 作成するページのデータ（title, slug, content, statusなど）
 * @param {string} tenantId - テナントID
 * @returns {Promise<object>} 作成されたページのIDとデータを含むオブジェクト
 */
export const createPage = async (pageData, userId = null, tenantId = null) => {
    try {
        // 現在のタイムスタンプを作成
        const now = Timestamp.now();
        const dataToSave = {
            ...pageData,
            // parentId が空文字列の場合は null として保存
            parentId: pageData.parentId === '' ? null : pageData.parentId,
            userId: userId, // ユーザーIDを追加
            accountId: userId, // アカウントIDも同じ値で設定
            tenantId: tenantId, // テナントIDを追加
            createdAt: now, // 作成日時を設定
            updatedAt: now, // 更新日時を設定
        };

        const docRef = await addDoc(pagesCollection, dataToSave);

        // 作成されたドキュメントのIDを含めて返す
        return { id: docRef.id, ...dataToSave };
    } catch (e) {
        console.error('Error adding document: ', e);
        throw e; // エラーを呼び出し元に再スロー
    }
};

/**
 * 既存のページをFirestoreで更新します。
 * @param {string} id - 更新するページのドキュメントID
 * @param {object} pageData - 更新するページのデータ
 * @returns {Promise<void>}
 */
export const updatePage = async (id, pageData) => {
    try {
        const pageRef = doc(db, 'pages', id);
        const now = Timestamp.now();

        // データをコピーして、undefined値を適切に処理
        const dataToUpdate = { ...pageData };

        // parentId の処理: undefined, null, 空文字列の場合は null として保存
        if (dataToUpdate.parentId === undefined || dataToUpdate.parentId === null || dataToUpdate.parentId === '') {
            dataToUpdate.parentId = null;
        }

        // updatedAt を設定
        dataToUpdate.updatedAt = now;

        // undefined値を含むフィールドを除外
        Object.keys(dataToUpdate).forEach(key => {
            if (dataToUpdate[key] === undefined) {
                delete dataToUpdate[key];
            }
        });

        await updateDoc(pageRef, dataToUpdate);

    } catch (e) {
        console.error('Error updating document: ', e);
        throw e;
    }
};

/**
 * 指定されたアカウントIDに紐づくすべてのページを取得します。
 * @param {string} userId - ページをフィルタリングするアカウントID
 * @param {object} options - オプションオブジェクト（pageSize, lastDoc, tenantId）
 * @returns {Promise<object>} ページデータの配列と最後のドキュメントとhasMoreフラグを含むオブジェクト
 */
export const getPages = async (userId, options = {}) => {
    try {

        const { pageSize = 10, lastDoc = null, statusFilter = null, tenantId = null } = options;
        const pagesRef = collection(db, 'pages');

        // authorIdでクエリを実行
        let conditions = [
            where('authorId', '==', userId),
            limit(pageSize)
        ];

        // テナントIDが指定されている場合はフィルタに追加
        if (tenantId) {
            conditions.push(where('tenantId', '==', tenantId));
        }

        // 続きのページを取得する場合
        if (lastDoc) {
            conditions.push(startAfter(lastDoc));
        }

        const q = query(pagesRef, ...conditions);
        const querySnapshot = await getDocs(q);

        // authorIdで結果が空の場合、accountIdで試す
        if (querySnapshot.empty) {

            const accountConditions = [
                where('accountId', '==', userId),
                limit(pageSize)
            ];

            // テナントIDが指定されている場合はフィルタに追加
            if (tenantId) {
                accountConditions.push(where('tenantId', '==', tenantId));
            }

            if (lastDoc) {
                accountConditions.push(startAfter(lastDoc));
            }

            const accountQuery = query(pagesRef, ...accountConditions);
            const accountSnapshot = await getDocs(accountQuery);



            const pages = [];
            const seenIds = new Set(); // 重複チェック用

            accountSnapshot.forEach((doc) => {
                const data = doc.data();
                // 重複を防ぐ
                if (!seenIds.has(doc.id)) {
                    seenIds.add(doc.id);

                    pages.push({
                        id: doc.id,
                        ...data
                    });
                }
            });

            // クライアント側でソート（更新日時順）
            pages.sort((a, b) => {
                const aTime = a.updatedAt?.toDate?.() || a.updatedAt || new Date(0);
                const bTime = b.updatedAt?.toDate?.() || b.updatedAt || new Date(0);
                return bTime - aTime;
            });

            const lastVisible = accountSnapshot.docs[accountSnapshot.docs.length - 1];

            const result = {
                pages,
                lastVisible,
                hasMore: accountSnapshot.docs.length === pageSize
            };


            return result;
        }

        const pages = [];
        const seenIds = new Set();

        querySnapshot.forEach((doc) => {
            const data = doc.data();
            // 重複を防ぐ
            if (!seenIds.has(doc.id)) {
                seenIds.add(doc.id);

                pages.push({
                    id: doc.id,
                    ...data
                });
            }
        });

        // クライアント側でソート（更新日時順）
        pages.sort((a, b) => {
            const aTime = a.updatedAt?.toDate?.() || a.updatedAt || new Date(0);
            const bTime = b.updatedAt?.toDate?.() || b.updatedAt || new Date(0);
            return bTime - aTime;
        });

        // 最後のドキュメントを返す（次のページ取得時に使用）
        const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];

        const result = {
            pages,
            lastVisible,
            hasMore: querySnapshot.docs.length === pageSize
        };



        return result;
    } catch (error) {
        console.error('Error fetching pages:', error);
        throw error;
    }
};

/**
 * 指定されたIDのページをFirestoreから取得します。
 * @param {string} id - 取得するページのドキュメントID
 * @returns {Promise<object|null>} ページデータオブジェクト、または見つからない場合はnull
 */
export const getPageById = async (id) => {
    try {

        const docRef = doc(db, 'pages', id);

        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            const pageData = processDocumentData(docSnap);
            return pageData;
        } else {
            return null;
        }
    } catch (e) {
        console.error('❌ Error getting document by ID:', e);
        console.error('🔍 エラー詳細:', {
            code: e.code,
            message: e.message,
            stack: e.stack
        });

        // 権限エラーの場合は詳細情報を出力
        if (e.code === 'permission-denied') {
            console.error('🚫 権限不足エラー - pageId:', id);
            console.error('🔐 認証状態:', 'ログインが必要です');
        }

        throw e;
    }
};

/**
 * 指定されたIDのページをFirestoreから削除します。
 * @param {string} id - 削除するページのドキュメントID
 * @returns {Promise<void>}
 */
export const deletePage = async (id) => {
    try {
        const pageRef = doc(db, 'pages', id);
        await deleteDoc(pageRef);

    } catch (e) {
        console.error('Error deleting document: ', e);
        throw e;
    }
};

export const getPageBySlug = async (slug) => {
    try {
        const pagesRef = collection(db, 'pages');
        const q = query(pagesRef, where('slug', '==', slug));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
            const doc = querySnapshot.docs[0];
            return processDocumentData(doc);
        }
        return null;
    } catch (error) {
        console.error('Error getting page by slug:', error);
        throw error;
    }
};

/**
 * 指定されたslugの公開ページをFirestoreから取得します。
 * @param {string} slug - 取得するページのslug
 * @returns {Promise<object|null>} 公開ページデータオブジェクト、または見つからない場合はnull
 */
export const getPublishedPageBySlug = async (slug) => {
    try {
        console.log('🔍 公開ページ検索:', { slug });

        const pagesRef = collection(db, 'pages');
        const q = query(
            pagesRef,
            where('slug', '==', slug),
            where('status', '==', 'published')
        );
        const querySnapshot = await getDocs(q);

        console.log('📊 検索結果:', { empty: querySnapshot.empty, size: querySnapshot.size });

        if (!querySnapshot.empty) {
            const doc = querySnapshot.docs[0];
            const pageData = processDocumentData(doc);
            console.log('✅ 公開ページ取得成功:', { id: pageData.id, title: pageData.title, slug: pageData.slug });
            return pageData;
        }

        console.log('❌ 公開ページが見つかりません:', slug);
        return null;
    } catch (error) {
        console.error('❌ 公開ページ取得エラー:', error);
        throw error;
    }
};

/**
 * 指定されたアカウントIDに紐づくページの総数を取得します。
 * @param {string} userId - ページをフィルタリングするアカウントID
 * @returns {Promise<number>} ページの総数
 */
export const getPagesCount = async (userId) => {
    try {
        const pagesRef = collection(db, 'pages');

        // まずauthorIdでクエリを実行
        try {
            const authorQuery = query(pagesRef, where('authorId', '==', userId));

            const authorSnapshot = await getDocs(authorQuery);

            if (authorSnapshot.size > 0) {
                return authorSnapshot.size;
            }
        } catch (authorError) {
            console.warn('⚠️ authorIdクエリエラー:', authorError);
        }

        // authorIdで結果が空の場合、accountIdで試す
        try {
            const accountQuery = query(pagesRef, where('accountId', '==', userId));
            const accountSnapshot = await getDocs(accountQuery);

            return accountSnapshot.size;
        } catch (accountError) {
            console.warn('⚠️ accountIdクエリエラー:', accountError);
        }

        // 両方のクエリが失敗した場合、0を返す
        console.warn('⚠️ すべてのクエリが失敗、0を返します');
        return 0;
    } catch (error) {
        console.error('❌ Error fetching pages count:', error);
        console.error('🔍 エラー詳細:', {
            code: error.code,
            message: error.message,
            stack: error.stack
        });

        // 権限エラーの場合は詳細情報を出力
        if (error.code === 'permission-denied') {
            console.error('🚫 権限不足エラー - 現在のユーザー:', userId);
            console.error('🔐 認証状態:', auth.currentUser);
            console.warn('⚠️ 権限エラー: ページ数取得をスキップします');
            return 0; // 権限エラーの場合は0を返す
        }

        throw error;
    }
};

// テナント情報を取得する関数
export const getTenantBySubdomain = async (subdomain) => {
    try {

        const tenantsRef = collection(db, 'tenants');
        const q = query(tenantsRef, where('subdomain', '==', subdomain));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
            const doc = querySnapshot.docs[0];
            const tenantData = processDocumentData(doc);

            return tenantData;
        }


        return null;
    } catch (error) {
        console.error('Error getting tenant by subdomain:', error);
        // 権限エラーの場合はnullを返す
        if (error.code === 'permission-denied') {
            console.warn('テナント権限チェックでエラーが発生しました:', error);
            return null;
        }
        throw error;
    }
};

// テナント情報を作成する関数
export const createTenant = async (tenantData) => {
    try {
        const now = Timestamp.now();
        const dataToSave = {
            ...tenantData,
            createdAt: now,
            updatedAt: now,
        };

        const tenantsRef = collection(db, 'tenants');
        const docRef = await addDoc(tenantsRef, dataToSave);

        return { id: docRef.id, ...dataToSave };
    } catch (e) {
        console.error('Error creating tenant: ', e);
        throw e;
    }
};

// テナント情報を更新する関数
export const updateTenant = async (id, tenantData) => {
    try {
        const tenantRef = doc(db, 'tenants', id);
        const now = Timestamp.now();
        const dataToUpdate = {
            ...tenantData,
            updatedAt: now,
        };
        await updateDoc(tenantRef, dataToUpdate);

    } catch (e) {
        console.error('Error updating tenant: ', e);
        throw e;
    }
};

// 設定を取得する関数
export const getSettings = async (userId) => {
    try {
        const settingsDoc = await getDoc(doc(db, 'settings', userId));
        if (settingsDoc.exists()) {
            return settingsDoc.data();
        }
        return {
            googleAnalyticsId: '',
            googleTagManagerId: '',
            faviconUrl: '',
            snsShareImageUrl: '',
        };
    } catch (error) {
        console.error('設定の取得に失敗しました:', error);
        throw error;
    }
};

/**
 * 指定されたユーザーIDに紐づくすべてのページを取得します。
 * @param {string} userId - ページを取得するユーザーID
 * @returns {Promise<Array>} ページデータの配列
 */
export const getPagesByUserId = async (userId) => {
    try {


        const pagesRef = collection(db, 'pages');

        // ユーザーIDでクエリ
        const q = query(
            pagesRef,
            where('userId', '==', userId)
        );

        const querySnapshot = await getDocs(q);



        const pages = [];
        querySnapshot.forEach((doc) => {
            const data = doc.data();

            pages.push({
                id: doc.id,
                ...data
            });
        });

        // クライアント側でソート（更新日時の降順）
        pages.sort((a, b) => {
            const aTime = a.updatedAt?.toDate?.() || a.updatedAt || new Date(0);
            const bTime = b.updatedAt?.toDate?.() || b.updatedAt || new Date(0);
            return bTime - aTime;
        });


        return pages;

    } catch (error) {
        console.error('getPagesByUserId: Error fetching pages:', error);
        throw error;
    }
};

/**
 * 指定されたアカウントIDに紐づくすべてのページを取得します。
 * @param {string} accountId - ページを取得するアカウントID
 * @returns {Promise<Array>} ページデータの配列
 */
export const getPagesByAccountId = async (accountId) => {
    try {
        const pagesRef = collection(db, 'pages');

        // アカウントIDでクエリ
        const q = query(
            pagesRef,
            where('accountId', '==', accountId)
        );

        const querySnapshot = await getDocs(q);

        const pages = [];
        querySnapshot.forEach((doc) => {
            const data = doc.data();

            pages.push({
                id: doc.id,
                ...data
            });
        });

        // クライアント側でソート（更新日時の降順）
        pages.sort((a, b) => {
            const aTime = a.updatedAt?.toDate?.() || a.updatedAt || new Date(0);
            const bTime = b.updatedAt?.toDate?.() || b.updatedAt || new Date(0);
            return bTime - aTime;
        });

        return pages;

    } catch (error) {
        console.error('getPagesByAccountId: Error fetching pages:', error);
        throw error;
    }
};


/**
 * サブドメインに関連する公開ページを取得します（CORS回避用）
 * @param {string} slug - ページのslug
 * @param {string} accountId - アカウントID（オプション）
 * @returns {Promise<object|null>} ページデータまたはnull
 */
export const getPublishedPageForSubdomain = async (slug, accountId = null) => {
    try {
        console.log('🌐 サブドメイン用公開ページ検索:', { slug, accountId });

        const pagesRef = collection(db, 'pages');
        let q;

        if (accountId) {
            // アカウント指定の場合
            q = query(
                pagesRef,
                where('slug', '==', slug),
                where('status', '==', 'published'),
                where('accountId', '==', accountId)
            );
        } else {
            // 全体から検索
            q = query(
                pagesRef,
                where('slug', '==', slug),
                where('status', '==', 'published')
            );
        }

        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
            const doc = querySnapshot.docs[0];
            const pageData = processDocumentData(doc);
            console.log('✅ サブドメイン用ページ取得成功:', {
                id: pageData.id,
                title: pageData.title,
                slug: pageData.slug,
                accountId: pageData.accountId
            });
            return pageData;
        }

        console.log('❌ サブドメイン用ページが見つかりません:', { slug, accountId });
        return null;
    } catch (error) {
        console.error('❌ サブドメイン用ページ取得エラー:', error);

        // 権限エラーの場合は詳細ログを出力
        if (error.code === 'permission-denied') {
            console.warn('🚫 権限不足: 公開ページの読み取りに失敗しました');
            console.warn('🔧 Firestoreルールで公開ページ（status: published）が認証なしで読み取り可能か確認してください');
        }

        // エラーを再スローせず、nullを返す（フォールバックを有効にする）
        return null;
    }
};